<template>
  <div class="page-header align-items-start min-vh-screen mbr-home">
    <span class="mask bg-white"></span>
    <div class="container p-0" v-if="isLoadDone">
      <div class="row">
        <div class="col-lg-12 col-md-8 col-12 mx-auto">
          <div class="card-body" style="padding: 0rem 0rem">
            <header class="mbr-home__bar">
              <div class="mbr-home__who">
                <span class="mbr-home__logo">
                  <img v-if="$store.state.merchantLogo" :src="$store.state.merchantLogo" :alt="$t('home.merchant_logo')" />
                  <img v-else src="../assets/img/logo.png" :alt="$t('home.merchant_logo')" />
                </span>
                <div class="mbr-home__greet">
                  <p class="mbr-home__caption">{{ $t('home.welcome_back') }}</p>
                  <h1 class="mbr-home__name">
                    {{
                      currentMember.name || currentMember.last_name
                        ? (currentMember.name ? currentMember.name : "")+" "+(currentMember.last_name ? currentMember.last_name : "")
                        : (currentMember.phone_number ? currentMember.phone_number : "")
                    }}
                  </h1>
                </div>
              </div>
              <!-- MBR093: the top-right slot is the language picker; the member QR stays on the bottom nav's MY QR -->
              <button
                type="button"
                class="mbr-home__lang"
                :aria-label="$t('language.title')"
                data-bs-toggle="modal"
                data-bs-target="#mbr-language-sheet"
              >
                <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg>
                <span>{{ $i18n.locale.toUpperCase() }}</span>
              </button>
            </header>
            <section v-if="!isOtpExpired" class="mbr-home__otp">
              <div class="mbr-home__otp-head">
                <span class="mbr-home__otp-label">{{ $t('home.otp') }}</span>
                <button
                  type="button"
                  class="mbr-home__otp-refresh"
                  :aria-label="$t('home.otp_refresh')"
                  :disabled="isReloading"
                  @click="reloadData"
                >
                  <i class="material-icons-round" :class="{ spin: isReloading }" aria-hidden="true">refresh</i>
                </button>
              </div>
              <p class="mbr-home__otp-code">{{ currentMember.otp }}</p>
              <i18n-t keypath="home.otp_expires" tag="p" scope="global" class="mbr-home__otp-note">
                <template #seconds>
                  <strong>{{ $t('home.n_seconds', { n: otpRemainSeconds }) }}</strong>
                </template>
              </i18n-t>
            </section>
            <div v-if="merchantMember.allow_setup_membership_tiering != 0">
              <section class="mbr-home__hero">
                <span class="mbr-home__ring mbr-home__ring--lg" aria-hidden="true"></span>
                <span class="mbr-home__ring mbr-home__ring--sm" aria-hidden="true"></span>
                <span class="mbr-home__glow" aria-hidden="true"></span>
                <div class="mbr-home__tier-row">
                  <button type="button" class="mbr-home__tier" @click="loyaltyTierBenefit()">
                    <span>{{ merchantMember.current_tier_name ? merchantMember.current_tier_name : $t('home.no_tier') }}</span>
                    <svg fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                  </button>
                  <img
                    v-if="merchantMember.current_tier_icon"
                    :src="merchantMember.current_tier_icon"
                    class="mbr-home__badge"
                    :alt="$t('home.tier_icon')"
                  />
                </div>
                <div v-if="isUpgradeTierByAdmissions" class="mbr-home__stats mbr-home__stats--single" @click="gotoTransaction">
                  <div class="mbr-home__stat">
                    <span class="mbr-home__caps">{{ $t('home.home_match_attended') }}</span>
                    <p class="mbr-home__stat-value">{{ merchantMember.total_visit }}</p>
                  </div>
                </div>
                <div
                  v-else
                  class="mbr-home__stats"
                  :class="{ 'mbr-home__stats--topup': showTopupButton, 'mbr-home__stats--topup-points': showTopupButton && merchantMember.reward_type == rewardPoint }"
                >
                  <div class="mbr-home__stat mbr-home__stat--wallet" @click="gotoTransaction">
                    <span class="mbr-home__caps">{{ $t('home.wallet') }}</span>
                    <p class="mbr-home__stat-value">{{ merchantMember.amount_format }}</p>
                  </div>
                  <!-- MBR126: shown only when the merchant turns it on (Top-up settings) and the Top-up screen itself would open -->
                  <button v-if="showTopupButton" type="button" class="mbr-home__topup" @click="gotoTopup">
                    {{ $t('home.top_up') }}
                  </button>
                  <div
                    v-if="merchantMember.reward_type == rewardPoint"
                    class="mbr-home__stat mbr-home__stat--split"
                    @click="gotoTransaction"
                  >
                    <span class="mbr-home__caps">{{ $t('home.points') }}</span>
                    <p class="mbr-home__stat-value">{{ merchantMember.point_format }}</p>
                  </div>
                </div>
                <div class="mbr-home__progress">
                  <div class="mbr-home__track">
                    <div class="mbr-home__fill" :style="{ width: merchantMember.level_percentage + '%' }"></div>
                  </div>
                  <button type="button" class="mbr-home__level" @click="loyaltyTierBenefit()">
                    <span>{{ translateLevelNote(merchantMember.level_note) }}</span>
                    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>
                  </button>
                </div>
              </section>
              <!-- notice text is backend HTML: "(7 Point expiring in <span style="color:#f00">36 days,</span> 22/10/2026)" -->
              <div
                v-if="!isUpgradeTierByAdmissions && (merchantMember.notice_transaction_point_expiry.length > 0 || merchantMember.notice_transaction_amount_expiry.length > 0)"
                class="mbr-home__notices"
              >
                <p
                  v-for="(text, index) in merchantMember.notice_transaction_point_expiry.concat(merchantMember.notice_transaction_amount_expiry)"
                  :key="index"
                  class="mbr-home__notice"
                >
                  <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span class="mbr-home__notice-text" v-html="translateExpiryNotice(text)"></span>
                </p>
              </div>
            </div>
            <div
              v-if="imagesSlide.length > 0"
              id="carouselExampleCaptions"
              class="carousel slide mbr-home__banner"
              data-bs-ride="carousel"
            >
              <div class="carousel-indicators mbr-home__dots">
                <button
                  v-for="(value, index) in imagesSlide"
                  :item="value"
                  :index="index"
                  :key="index"
                  type="button"
                  data-bs-target="#carouselExampleCaptions"
                  :data-bs-slide-to="index"
                  v-bind:class="index == 0 ? 'active' : ''"
                  :aria-current="index == 0 ? 'true' : ''"
                  :aria-label="$t('home.slide_n', { n: index + 1 })"
                ></button>
              </div>
              <div class="carousel-inner">
                <div
                  v-for="(value, index) in imagesSlide"
                  :item="value"
                  :index="index"
                  :key="index"
                  class="carousel-item"
                  v-bind:class="index == 0 ? 'active' : ''"
                >
                  <a
                    :href="
                      value.destination_link
                        ? value.destination_link
                        : 'javascript:;'
                    "
                    :target="value.destination_link && '_blank'"
                  >
                    <img
                      :src="value.file_url"
                      class="d-block w-100 mobile-slide"
                    />
                  </a>
                </div>
              </div>
              <button
                class="carousel-control-prev"
                type="button"
                data-bs-target="#carouselExampleCaptions"
                data-bs-slide="prev"
              >
                <span
                  class="carousel-control-prev-icon"
                  aria-hidden="true"
                ></span>
                <span class="visually-hidden">{{ $t('common.previous') }}</span>
              </button>
              <button
                class="carousel-control-next"
                type="button"
                data-bs-target="#carouselExampleCaptions"
                data-bs-slide="next"
              >
                <span
                  class="carousel-control-next-icon"
                  aria-hidden="true"
                ></span>
                <span class="visually-hidden">{{ $t('common.next') }}</span>
              </button>
            </div>
            <div class="mbr-home__section">
              <h2 class="mbr-home__section-title">
                {{ $t('home.available_to_redeem', { count: merchantVouchers.length }) }}
              </h2>
            </div>
            <div v-if="merchantMember.share_info_profile == 0" class="mbr-home__profile">
              <img src="../assets/img/reward.png" class="mbr-home__profile-img" alt="" />
              <div class="mbr-home__profile-body">
                <p class="mbr-home__profile-text">
                  {{ $t('home.complete_profile') }}
                </p>
                <material-button size="sm" @click="editProfile()">
                  {{ $t('home.add_profile') }}
                </material-button>
              </div>
            </div>
            <!-- Home-local cards (VoucherCard stays as is for Store / MyRewards / …); same conditions as VoucherCard with isReward=false -->
            <div v-if="merchantVouchers.length > 0" class="mbr-home__rewards">
              <article
                v-for="(value, index) in merchantVouchers"
                :key="index"
                class="mbr-home__reward"
                :class="{ 'mbr-home__reward--locked': !canGetReward(value) }"
              >
                <img
                  :src="value.icon_url"
                  class="mbr-home__reward-img"
                  alt=""
                  @click="!value.is_bundle && rewardDetail(value.id)"
                />
                <div class="mbr-home__reward-body" @click="!value.is_bundle && rewardDetail(value.id)">
                  <h3 class="mbr-home__reward-title">{{ value.reward_title ?? "" }}</h3>
                  <p
                    v-if="merchantMember.reward_type == 1 || value.is_bundle"
                    class="mbr-home__reward-price"
                  >
                    {{ value.amount_text }}
                  </p>
                  <p
                    v-if="(merchantMember.reward_type == rewardPoint && !value.is_bundle) || (merchantMember.reward_type == rewardPoint && value.is_bundle && value.points_convert > 0)"
                    class="mbr-home__reward-price"
                  >
                    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path stroke-linecap="round" stroke-linejoin="round" d="M12 7v10M8.5 10.5h7" /></svg>
                    <span>{{ value.point_text }}</span>
                  </p>
                  <p v-if="value.is_bundle" class="mbr-home__reward-meta">{{ voucherCountText(value) }}</p>
                  <p class="mbr-home__reward-meta">{{ validityText(value.expiry_date_text) }}</p>
                </div>
                <button
                  v-if="canGetReward(value)"
                  type="button"
                  class="mbr-home__reward-get"
                  data-bs-toggle="modal"
                  data-bs-target="#bottom_modal"
                  @click="exchange(value)"
                >
                  {{ $t('common.get') }}
                </button>
                <button v-else type="button" class="mbr-home__reward-get" disabled>
                  {{ $t('common.get') }}
                </button>
              </article>
            </div>
            <div v-else class="mbr-home__empty">
              {{ $t('home.no_rewards') }}
            </div>
          </div>
        </div>
      </div>
      <div class="position-fixed top-1 end-1 z-index-2">
        <material-snackbar
          v-if="snackbar === 'danger' || snackbar === 'success'"
          title=""
          date=""
          :description="message"
          :icon="{ component: 'campaign', color: 'white' }"
          :color="snackbar"
          :close-handler="closeSnackbar"
        />
      </div>
    </div>
    <div v-else-if="loadFailed" class="mbr-home__error" role="alert">
      <span class="mbr-home__error-icon" aria-hidden="true">
        <i class="material-icons-round">cloud_off</i>
      </span>
      <h2 class="mbr-home__error-title">{{ $t('app.load_failed') }}</h2>
      <p class="mbr-home__error-sub">{{ $t('legal.check_connection') }}</p>
      <material-button class="mbr-home__error-retry" :disabled="loaderActive" @click="getDataHomePage()">{{ $t('legal.try_again') }}</material-button>
    </div>
    <MemorizeYourBirthday />
    <QrcodeMember />
    <MbrLanguageSheet />
    <MbrBottomNav />
    <!-- Modal -->
    <Exchange
      :rewardPerTransaction="merchantMember.reward_type ?? 0"
      :voucherData="previewVoucherExchange"
      :amount_format="merchantMember.amount_format"
      :amount_in_account="merchantMember.amount_in_account"
      :point_format="merchantMember.point_format"
      :point_in_account="merchantMember.point"
      :confirm-exchange="confirmExchange"
    />
    <material-loading :active="loaderActive" />
    <ModalConfirm :message="$t('reward.confirm_exchange')"
            :btnYes="$t('common.yes')" :sycn-profile="processExchange" />
    <ModalSuccess
      :processAcction="gotoMyReward"
      :message="$t('reward.exchange_success')"
      :buttonText="$t('reward.go_to_my_reward')"
    />
  </div>
</template>

<script>
import { mapMutations, mapActions, mapState } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import HomeService from "../services/home.service";
import "bootstrap/dist/js/bootstrap.bundle";
import MaterialButton from "@/components/MaterialButton.vue";
import Exchange from "@/components/Exchange.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import ModalSuccess from "@/components/ModalSuccess.vue";
import ModalConfirm from "@/components/ModalConfirm.vue";
import "bootstrap/js/dist/modal";
import $ from "jquery";
import MemorizeYourBirthday from '@/components/MemorizeYourBirthday.vue'
import QrcodeMember from "@/components/QrcodeMember.vue";
import MbrBottomNav from "@/components/MbrBottomNav.vue";
import MbrLanguageSheet from "@/components/MbrLanguageSheet.vue";
import { getMerchantCode, localStorageName } from "@/config/utils";
import MemberService from "@/services/member.service";
import TokenService from "@/services/token.service";
import { translateLevelNote, translateExpiryNotice, voucherCountText, validityText } from "@/i18n/apiText";
import { errorMessage } from "@/utils/errorMessage";

export default {
  name: "my-transacions",
  components: {
    MaterialSnackbar,
    MaterialButton,
    Exchange,
    MaterialLoading,
    ModalSuccess,
    MemorizeYourBirthday,
    ModalConfirm,
    QrcodeMember,
    MbrBottomNav,
    MbrLanguageSheet
  },
  data() {
    return {
      listTransaction: [],
      membershipCashback: 0,
      transactionType: [],
      imagesSlide: [],
      merchantVouchers: [],
      currentMember: {},
      merchantMember: {},
      previewVoucherExchange: {},
      snackbar: null,
      message: "",
      loaderActive: false,
      loadFailed: false,
      merchantCode: "",
      isLoadDone: false,
      rewardPoint: 2,
      isUpgradeTierByAdmissions: 0,
      nowTs: Date.now(),
      otpInterval: null,
      isReloading: false,
      memberPermissions: [],
    };
  },
  created() {
    this.getDataHomePage();

    var session_id = this.$route.query.session_id;
    var reward_id = this.$route.query.reward_id;
    if(session_id && reward_id) { // call back after pay success - stripe
      this.showLoader();
      this.isLoadDone = false;
      HomeService.processExchangeByCard(reward_id, session_id).then(
        (response) => {
          if (response.data.result.isSuccess) {
            // const merchantCode = localStorage.getItem('MERCHANT_CODE', '');
            // var url = location.protocol + '//' + location.host+"/"+merchantCode+"/home";
            // window.location.href = url;
            this.getDataHomePage();
            this.snackbar = "success";
            this.message = this.$t("reward.exchange_successfully");
            this.setTimeOutNotification();
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.isLoadDone = true;
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    }

    if (window.innerWidth <= 1200) {
      if ($("#app").hasClass("g-sidenav-pinned")) {
        this.toggleSidebar();
      }
    }
  },
  mounted() {
    const code = getMerchantCode();
    this.merchantCode = code ? `/${code}` : "";

    // same permission the Top-up row in Account / the sidenav checks
    if (TokenService.getLocalAccessToken()) {
      MemberService.checkMemberPermissions().then(
        (response) => {
          if (response.data.result.isSuccess && Array.isArray(response.data.data)) {
            this.memberPermissions = response.data.data;
          }
        },
        (error) => {
          console.log(error);
        }
      );
    }

    const memorize = this.$route.query.memorize ?? null;
    if (memorize) {
      $(".bottom_modal_memorize").click();
    }
  },
  // Home draws its own header like the design: hide the top navbar. The sidenav stays mounted
  // but is an off-screen drawer at every width (#app.mbr-sidenav-overlay in custom.css), so the
  // bottom nav's ACCOUNT button can slide it in.
  beforeMount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.add("mbr-sidenav-overlay");
    app.classList.remove("g-sidenav-pinned");
  },
  beforeUnmount() {
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
    }
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    // back to the legacy layout: App.vue pins the sidenav above 1200px
    const app = document.querySelector("#app");
    app.classList.remove("mbr-sidenav-overlay");
    if (window.innerWidth > 1200) {
      app.classList.add("g-sidenav-pinned");
    }
  },
  computed: {
    showTopupButton() {
      return localStorage.getItem(localStorageName("SHOW_TOPUP_BUTTON_ON_HOME")) == 1
        && localStorage.getItem(localStorageName("IS_CHECK_MERCHANT_CONNECTED_STRIPE")) === "true"
        && this.memberPermissions.includes("Top-up");
    },

    otpExpireTs() {
      return this.currentMember?.otp_expire
        ? new Date(this.currentMember.otp_expire).getTime()
        : 0;
    },

    isOtpExpired() {
      return this.nowTs >= this.otpExpireTs;
    },

    otpRemainSeconds() {
      if (!this.otpExpireTs) return 0;
      return Math.max(
        Math.floor((this.otpExpireTs - this.nowTs) / 1000),
        0
      );
    }
  },
  methods: {
    translateLevelNote,
    translateExpiryNotice,
    voucherCountText,
    validityText,
    ...mapMutations([
      "toggleNavbarOnly",
      "toggleHideConfig",
      "navbarMinimize",
    ]),
    ...mapActions(["setlabelColor", "setLabelColor", "setButtonColor"]),
    ...mapState(["isPinned"]),
    gotoMyReward() {
      $(".btn-close").click();
      this.$router.push(this.merchantCode + "/my-rewards");
    },
    toggleSidebar() {
      this.navbarMinimize();
    },
    editProfile() {
      this.$router.push(this.merchantCode + "/profile");
    },

    exchange(voucher) {
      this.previewVoucherExchange = voucher;
    },

    // same rule VoucherCard uses to show "Get" (isReward=false)
    canGetReward(voucher) {
      return !voucher.is_disabled || (voucher.is_bundle && !voucher.is_disabled_by_card);
    },

    closeSnackbar() {
      this.snackbar = null;
    },

    rewardDetail(reward_id) {
      this.$router.push(this.merchantCode + "/reward-detail/" + reward_id);
    },

    gotoTopup() {
      // MBR126: Top-up's back button returns here instead of Account
      this.$router.push({ path: this.merchantCode + "/top-up", query: { from: "home" } });
    },
    gotoTransaction() {
      this.$router.push(this.merchantCode + "/my-transactions");
    },
    getDataHomePage() {
      this.closeSnackbar();
      this.getDataHomePageCommon();
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },

    loyaltyTierBenefit() {
      this.$router.push(this.merchantCode + "/loyalty-tier-benefit");
    },

    getDataHomePageCommon() {
      this.showLoader();
      this.isLoadDone = false;
      HomeService.getDataHomePage().then(
        (response) => {
          if (response.data.result.isSuccess) {
            if(response.data.data.member && !response.data.data.member.phone_number) {
              this.$router.push(this.merchantCode + "/profile");
            }
            this.imagesSlide = response.data.data.slides;
            this.merchantVouchers = response.data.data.vouchers;
            this.currentMember = response.data.data.member;
            this.merchantMember = response.data.data.merchant_member;
            this.isUpgradeTierByAdmissions = parseInt(response.data.data.merchant_member.upgrade_tier_by_admissions);
            this.loadFailed = false;
            this.isLoadDone = true;
            this.hideLoader();
          } else {
            this.onHomeLoadFailed(errorMessage(response.data.result.message));
          }
        },
        (error) => {
          this.onHomeLoadFailed(error.response?.data?.result?.message);
        }
      );
    },

    // MBR121: without home data the template can't render (blank page). A refresh that fails keeps
    // what is already on screen and shows the snackbar; a first load that fails shows the error state.
    onHomeLoadFailed(message) {
      if (Object.keys(this.merchantMember ?? {}).length > 0) {
        this.isLoadDone = true;
        this.snackbar = "danger";
        this.message = message || this.$t("app.load_failed");
        this.setTimeOutNotification();
      } else {
        this.loadFailed = true;
      }
      this.hideLoader();
    },

    confirmExchange() {
      $('.btn-confirm-modal').click();
    },

    processExchange() {
      $('.btn-close-confirm-modal').click();
      this.closeSnackbar();
      this.showLoader();
      const rewardId = this.previewVoucherExchange.id ?? 0;
      HomeService.processExchange(rewardId).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.getDataHomePageCommon();
            $(".btn-close").click();
            $(".btn-success-modal").click();
          } else {
            $(".btn-close").click();
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
    startOtpTimer() {
      if (this.otpInterval) {
        clearInterval(this.otpInterval);
      }

      this.otpInterval = setInterval(() => {
        this.nowTs = Date.now();
      }, 1000);
    },
    reloadData() {
      if (this.isReloading) return;

      this.isReloading = true;

      this.getDataHomePageCommon();

      setTimeout(() => {
        this.isReloading = false;
      }, 500);
    },
  },
  watch: {
    currentMember(val) {
      if (val?.otp_expire) {
        this.startOtpTimer();
      }
    }
  },
};
</script>
<!--
  MBR022: the former unscoped block is gone. Its rules are still loaded identically by Store.vue and
  RewardCatalogue.vue (same unscoped copies); .mobile-slide and the mobile h2 size moved to custom.css;
  .tab-btn / .carousel-indicators / .font-level-member-extra were only used by the old Home markup.
-->
<style scoped>
/* MBR018 Home — member_app_UI/song_fa_members_member_home */

/* Material Dashboard's `.page-header { overflow: hidden }` would stop the header sticking */
.mbr-home.page-header {
  overflow: visible;
  overflow-x: clip;
  /* room for the fixed MbrBottomNav */
  padding-bottom: 120px;
  font-family: var(--mbr-font-body);
}

.mbr-home .mask {
  background: var(--mbr-color-canvas) !important;
  opacity: 1;
}

/* Mobile column like the design (and MyProfile) */
.mbr-home .container {
  max-width: 440px;
  margin: 0 auto;
}

.mbr-home__bar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 24px 20px 16px;
  background: rgba(var(--mbr-color-canvas-rgb), 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(var(--mbr-color-outline-rgb), 0.4);
}

.mbr-home__who {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.mbr-home__logo {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 6px;
  background: var(--mbr-color-primary);
  border-radius: var(--mbr-radius-lg);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-shadow-rgb), 0.05);
}

/* inner radius = tile radius (16px) - padding (6px), so the logo corners follow the tile */
.mbr-home__logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 10px;
}

.mbr-home__greet {
  min-width: 0;
}

.mbr-home__caption {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  color: var(--mbr-color-ink-muted);
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-home__name {
  margin: 4px 0 0;
  overflow: hidden;
  font-family: var(--mbr-font-headline);
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: normal;
  color: var(--mbr-color-ink-headline);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mbr-home__lang {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 48px;
  padding: 0 16px 0 14px;
  background: var(--mbr-color-primary);
  border: 0;
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-on-primary);
  box-shadow: var(--mbr-shadow-card);
  font-family: var(--mbr-font-headline);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  transition: transform 0.15s ease;
}
.mbr-home__lang:active {
  transform: scale(0.95);
}
.mbr-home__lang svg {
  width: 22px;
  height: 22px;
}

.mbr-home__otp {
  margin: 16px 20px 0;
  padding: 14px 16px 16px;
  background: var(--mbr-color-surface);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
  border-radius: var(--mbr-radius-lg);
  box-shadow: var(--mbr-shadow-card);
}

.mbr-home__otp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mbr-home__otp-label {
  font-size: var(--mbr-label-caps-size);
  font-weight: var(--mbr-label-caps-weight);
  line-height: var(--mbr-label-caps-line);
  letter-spacing: var(--mbr-label-caps-tracking);
  text-transform: uppercase;
  color: var(--mbr-color-ink-muted);
}

.mbr-home__otp-refresh {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: var(--mbr-color-tint);
  border: 0;
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-primary);
}

.mbr-home__otp-refresh .material-icons-round {
  font-size: 18px;
}

.mbr-home__otp-refresh .spin {
  animation: mbr-home-spin 0.5s linear infinite;
}

@keyframes mbr-home-spin {
  to {
    transform: rotate(360deg);
  }
}

.mbr-home__otp-code {
  margin: 10px 0 0;
  padding: 10px 0;
  background: var(--mbr-color-tint);
  border-radius: var(--mbr-radius-md);
  font-family: var(--mbr-font-headline);
  font-size: 30px;
  font-weight: 800;
  line-height: 36px;
  letter-spacing: 10px;
  text-align: center;
  /* letter-spacing trails the last digit: pad the start to stay centred */
  text-indent: 10px;
  color: var(--mbr-color-primary);
}

.mbr-home__otp-note {
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-home__otp-note strong {
  font-weight: 700;
  color: var(--mbr-color-primary);
}

/* MBR020 hero card */
.mbr-home__hero {
  position: relative;
  margin: 16px 20px 0;
  padding: 24px;
  overflow: hidden;
  background: var(--mbr-color-primary);
  border-radius: var(--mbr-radius-xl);
  color: var(--mbr-color-on-primary);
  box-shadow: var(--mbr-shadow-card);
}

.mbr-home__hero > :not([aria-hidden]) {
  position: relative;
  z-index: 1;
}

.mbr-home__ring {
  position: absolute;
  border: 1px solid rgba(var(--mbr-color-gold-rgb), 0.25);
  border-radius: var(--mbr-radius-full);
  pointer-events: none;
}

.mbr-home__ring--lg {
  right: -32px;
  bottom: -32px;
  width: 176px;
  height: 176px;
}

.mbr-home__ring--sm {
  right: -8px;
  bottom: -8px;
  width: 112px;
  height: 112px;
  border-color: rgba(var(--mbr-color-gold-rgb), 0.15);
}

.mbr-home__glow {
  position: absolute;
  top: 16px;
  right: 48px;
  width: 80px;
  height: 80px;
  background: rgba(var(--mbr-color-gold-rgb), 0.05);
  border-radius: var(--mbr-radius-full);
  filter: blur(24px);
  pointer-events: none;
}

.mbr-home__caps {
  display: block;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(var(--mbr-color-peach-rgb), 0.7);
}

.mbr-home__id-value {
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.05em;
  color: var(--mbr-color-on-primary);
}

.mbr-home__stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(var(--mbr-color-surface-rgb), 0.1);
  cursor: pointer;
}

.mbr-home__stats--single {
  grid-template-columns: 1fr;
}

/* MBR021 tier pill, badge and progress */
.mbr-home__tier-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.mbr-home__tier {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 6px 14px;
  background: var(--mbr-color-primary-light);
  border: 1px solid rgba(var(--mbr-color-gold-rgb), 0.35);
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: var(--mbr-color-peach);
}

.mbr-home__tier span {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mbr-home__tier svg {
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
}

.mbr-home__badge {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  object-fit: cover;
  background: var(--mbr-color-peach);
  border: 2px solid var(--mbr-color-gold);
  border-radius: var(--mbr-radius-full);
}

.mbr-home__id {
  margin-top: 12px;
}

.mbr-home__progress {
  margin-top: 20px;
}

.mbr-home__track {
  width: 100%;
  height: 8px;
  overflow: hidden;
  background: rgba(var(--mbr-color-surface-rgb), 0.2);
  border-radius: var(--mbr-radius-full);
}

.mbr-home__fill {
  max-width: 100%;
  height: 100%;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  transition: width 0.5s ease;
}

.mbr-home__level {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  margin-top: 8px;
  padding: 0;
  background: none;
  border: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: left;
  color: var(--mbr-color-peach);
}

.mbr-home__level svg {
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  opacity: 0.8;
}

.mbr-home__stat {
  min-width: 0;
}

.mbr-home__stat--split {
  padding-left: 8px;
  border-left: 1px solid rgba(var(--mbr-color-surface-rgb), 0.1);
}

/* MBR126 Top-up pill on the right of the wallet balance */
.mbr-home__stats--topup {
  grid-template-columns: 1fr auto;
  align-items: center;
}

/* with the points column there is no room on the row: the pill goes under the balance */
.mbr-home__stats--topup-points {
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    "wallet points"
    "topup points";
  row-gap: 10px;
  align-items: start;
}

.mbr-home__stats--topup-points .mbr-home__stat--wallet {
  grid-area: wallet;
}

.mbr-home__stats--topup-points .mbr-home__stat--split {
  grid-area: points;
}

.mbr-home__stats--topup-points .mbr-home__topup {
  grid-area: topup;
  justify-self: start;
}

.mbr-home__topup {
  padding: 10px 22px;
  background: var(--mbr-color-surface);
  border: 0;
  border-radius: var(--mbr-radius-full);
  box-shadow: var(--mbr-shadow-card);
  font-family: var(--mbr-font-headline);
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;
  color: var(--mbr-color-primary);
}

.mbr-home__topup:active {
  transform: scale(0.97);
}

.mbr-home__stat-value {
  margin: 2px 0 0;
  overflow-wrap: anywhere;
  font-family: var(--mbr-font-headline);
  font-size: 26px;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.025em;
  color: var(--mbr-color-on-primary);
}

.mbr-home__notices {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 16px 20px 0;
}

.mbr-home__notice {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 12px 16px;
  background: var(--mbr-color-tint);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
  border-radius: var(--mbr-radius-lg);
  font-size: 12px;
  line-height: 16px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-home__notice svg {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  color: var(--mbr-color-primary);
}

/* backend paints the days red inline */
.mbr-home__notice-text :deep(span) {
  font-weight: 700;
  color: var(--mbr-color-primary) !important;
}

/* MBR022 banner */
.mbr-home__banner {
  margin: 20px 20px 0;
  overflow: hidden;
  border-radius: var(--mbr-radius-xl);
  box-shadow: var(--mbr-shadow-card);
}

.mbr-home__banner .mobile-slide {
  object-fit: cover;
}

.mbr-home__dots {
  bottom: 10px;
  margin-bottom: 0;
}

.mbr-home__dots [data-bs-target] {
  width: 6px;
  height: 6px;
  margin: 0 3px;
  padding: 0;
  background-color: var(--mbr-color-primary);
  border: 0;
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 0 0 1px rgba(var(--mbr-color-surface-rgb), 0.7);
  opacity: 0.5;
  transition: width 0.2s ease, opacity 0.2s ease;
}

.mbr-home__dots .active {
  width: 18px;
  opacity: 1;
}

/* MBR022 section heading + profile prompt */
.mbr-home__section {
  margin: 28px 20px 0;
}

.mbr-home__section-title {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: 22px;
  font-weight: 800;
  line-height: 28px;
  letter-spacing: -0.01em;
  color: var(--mbr-color-ink-headline);
}

.mbr-home__profile {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 12px 20px 0;
  padding: 12px;
  background: var(--mbr-color-card);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
  border-radius: var(--mbr-radius-lg);
  box-shadow: var(--mbr-shadow-card);
}

.mbr-home__profile-img {
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  object-fit: contain;
}

.mbr-home__profile-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.mbr-home__profile-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--mbr-color-ink-headline);
}

/* MBR022 reward cards */
.mbr-home__rewards {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 12px 20px 0;
  padding-bottom: 32px;
}

.mbr-home__reward {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px;
  background: var(--mbr-color-card);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
  border-radius: var(--mbr-radius-lg);
  box-shadow: var(--mbr-shadow-card);
}

.mbr-home__reward--locked {
  background: rgba(var(--mbr-color-surface-rgb), 0.7);
  box-shadow: none;
  opacity: 0.8;
}

.mbr-home__reward-img {
  flex: 0 0 auto;
  width: 88px;
  height: 88px;
  object-fit: cover;
  background: var(--mbr-color-tint);
  border-radius: var(--mbr-radius-md);
  cursor: pointer;
}

.mbr-home__reward-body {
  flex: 1 1 auto;
  min-width: 0;
  cursor: pointer;
}

.mbr-home__reward-title {
  margin: 0;
  overflow: hidden;
  font-family: var(--mbr-font-headline);
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  /* Material Dashboard tightens h1-h6 */
  letter-spacing: normal;
  color: var(--mbr-color-ink-headline);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mbr-home__reward-price {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 4px 0 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: var(--mbr-color-primary);
}

.mbr-home__reward-price svg {
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
}

.mbr-home__reward--locked .mbr-home__reward-price {
  font-weight: 600;
  color: var(--mbr-color-ink-muted);
}

.mbr-home__reward-meta {
  margin: 2px 0 0;
  font-size: 11px;
  line-height: 16px;
  color: var(--mbr-color-ink-muted);
}

.mbr-home__reward-get {
  flex: 0 0 auto;
  padding: 6px 16px;
  background: var(--mbr-color-primary);
  border: 1px solid transparent;
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: var(--mbr-color-on-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-shadow-rgb), 0.05);
  transition: transform 0.15s ease;
}

.mbr-home__reward-get:active:not(:disabled) {
  transform: scale(0.95);
}

.mbr-home__reward-get:disabled {
  background: var(--mbr-color-tint-dark);
  border-color: rgba(var(--mbr-color-outline-rgb), 0.6);
  font-family: var(--mbr-font-body);
  font-weight: 600;
  color: var(--mbr-color-ink-muted);
  box-shadow: none;
  cursor: not-allowed;
}

/* MBR121: /member/home failed on first load — same look as the App/Privacy load error */
.mbr-home__error {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: calc(100vh - 120px);
  padding: 24px 16px;
  text-align: center;
}

.mbr-home__error-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 8px;
  background: var(--mbr-color-tint);
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-primary);
}

.mbr-home__error-icon .material-icons-round {
  font-size: 32px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-home__error-title {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: var(--mbr-headline-sm-size);
  font-weight: 700;
  line-height: var(--mbr-headline-sm-line);
  color: var(--mbr-color-ink-headline);
}

.mbr-home__error-sub {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--mbr-color-ink-muted);
}

.mbr-home__error-retry {
  min-width: 160px;
  margin-top: 12px;
}

.mbr-home__empty {
  margin: 12px 20px 32px;
  padding: 40px 16px;
  background: var(--mbr-color-tint);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
  border-radius: var(--mbr-radius-lg);
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  color: var(--mbr-color-ink-muted);
}
</style>